import { Button, Radio, Spin } from "antd";
import dayjs from "dayjs";
import { DateListPicker } from "~/components/form/DateListPicker.jsx";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { _parseDate } from "~/lib/localize.js";

function getAvailableSlotsForDate(date, product, bookedTimeslots, ecetTime) {
  if (!date || !product) return [];
  const calendarAvailability = product.calendar_availability || {};
  const duration = parseInt(product.duration, 10) || 60;
  const weekday = dayjs(date).locale("en").format("dddd").toLowerCase();
  const slotRanges = calendarAvailability[weekday] || [];

  const bookedForDate =
    bookedTimeslots && typeof bookedTimeslots[date] === "object"
      ? bookedTimeslots[date]
      : {};

  const slots = [];

  slotRanges.forEach(([start, end]) => {
    // Determine effective end time:
    // - string → plain HH:mm end time
    // - number → ECET + offset (offset is negative = N min before ECET)
    let endTime;
    if (typeof end === "number") {
      if (!ecetTime) return; // ECET module not loaded yet; skip this range
      endTime = dayjs(date + " " + ecetTime).add(end, "minute");
    } else {
      endTime = dayjs(date + " " + end);
    }

    let current = dayjs(date + " " + start);
    while (current.isBefore(endTime)) {
      // Skip slot if it would end after the effective boundary
      if (current.add(duration, "minute").isAfter(endTime)) break;
      const slotStart = current.format("HH:mm");
      const slotEnd = current.add(duration, "minute").format("HH:mm");
      const number_booked = bookedForDate[slotStart] || 0;

      let disabled = false;
      if (product.max_per_slot > 0 && number_booked >= product.max_per_slot) {
        disabled = true;
      }

      let color = "#52c41a"; // green
      if (product.max_per_slot > 0) {
        if (number_booked >= product.max_per_slot)
          color = "#ff4d4f"; // red
        else if (number_booked > 0) color = "#faad14"; // yellow
      }

      slots.push({
        label: (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 100 }}>
              {slotStart} - {slotEnd}
            </span>
            <span
              style={{
                display: "inline-block",
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: color,
              }}
            />
            {product.max_per_slot > 0
              ? ` ${number_booked} / ${product.max_per_slot}`
              : ""}
          </span>
        ),
        value: slotStart,
        disabled,
        sortKey: slotStart,
      });
      current = current.add(duration, "minute");
    }
  });

  slots.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return slots;
}

/** Parse a reserved_timeslot string into { date, timeslot } */
function parseTimeslotValue(val) {
  if (!val) return { date: null, timeslot: null };
  // Normalise: strip timezone offset, handle both "T" and " " separators
  const normalized = val.split("+")[0];
  const sep = normalized.includes("T") ? "T" : " ";
  const parts = normalized.split(sep);
  return {
    date: parts[0] || null,
    timeslot: parts[1] ? parts[1].slice(0, 5) : null,
  };
}

/**
 * TimeslotPicker — a form-compatible component for selecting a date + timeslot.
 *
 * Props:
 *   value             : string | null  — "YYYY-MM-DDTHH:mm" or null
 *   onChange          : (value: string | null) => void
 *   product           : object  — prepaid product (id, calendar_availability, duration, max_per_slot)
 *   retrieveTimeslots : boolean — when true, fetch available days + booked slots from the API
 */
export function TimeslotPicker({
  voucherId,
  value,
  onChange,
  product,
  retrieveTimeslots = false,
}) {
  const { date: parsedDate, timeslot: parsedTimeslot } =
    parseTimeslotValue(value);

  const { club } = useSelector((state) => state.persist);

  const [selectedDate, setSelectedDate] = useState(parsedDate);
  const [selectedTimeslot, setSelectedTimeslot] = useState(parsedTimeslot);
  const [availableDays, setAvailableDays] = useState([]);
  const [bookedTimeslots, setBookedTimeslots] = useState({});
  const [loading, setLoading] = useState(false);

  // Lazy-load calculateECET_Meeus once on mount
  const calculateEcetFnRef = useRef(null);
  const [calculateEcetFn, setCalculateEcetFn] = useState(null);
  useEffect(() => {
    import("~/components/topmeteo/ECET.jsx").then((m) => {
      calculateEcetFnRef.current = m.calculateECET_Meeus;
      setCalculateEcetFn(() => m.calculateECET_Meeus);
    });
  }, []);

  // Recalculate ECET synchronously whenever the selected date changes
  const ecetTime = useMemo(() => {
    if (!selectedDate || !calculateEcetFn || !club) return null;
    return (
      calculateEcetFn(
        selectedDate,
        club.vliegveld_lon,
        club.vliegveld_lat,
        true,
        club.country,
      ) || null
    );
  }, [selectedDate, calculateEcetFn, club]);

  useEffect(() => {
    const parsed = parseTimeslotValue(value);
    setSelectedDate(parsed.date);
    setSelectedTimeslot(parsed.timeslot);
  }, [value]);

  useEffect(() => {
    if (!retrieveTimeslots || !product?.id) return;
    (async () => {
      setLoading(true);
      const _d = await api.get(
        `financial/prepaid-products/${product.id}/booked_timeslots.json?voucher_id=${voucherId === "new" ? "0" : voucherId}`,
      );
      if (_d) {
        const bookedByDate = (_d.slots || [])
          .filter((s) => s.reserved_timeslot)
          .reduce((acc, s) => {
            const [d, time] = s.reserved_timeslot.split("T");
            const timeStr = time.slice(0, 5);
            if (!acc[d]) acc[d] = {};
            acc[d][timeStr] = s.num_account_holders;
            return acc;
          }, {});
        setBookedTimeslots(bookedByDate);

        // filter only days with options to book in
        const days =
          _d.available_days.filter((d) => {
            const weekday = dayjs(d).locale("en").format("dddd").toLowerCase();
            return (product?.calendar_availability?.[weekday] || []).length > 0;
          }) || [];

        setAvailableDays(days);

        // Auto-select the first available date + first slot
        if (!selectedDate && days.length > 0) {
          setSelectedDate(days[0]);
        }
      }
      setLoading(false);
    })();
  }, [product?.id, retrieveTimeslots]);

  const dayOptions = [
    // If selectedDate isn't in availableDays yet (before fetch), inject it so the Select
    // can display a formatted label instead of the raw YYYY-MM-DD string.
    ...(selectedDate && !availableDays.includes(selectedDate)
      ? [
          {
            label: _parseDate(selectedDate, {
              weekday: "long",
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
            value: selectedDate,
          },
        ]
      : []),
    ...availableDays.map((d) => ({
      label: _parseDate(d, {
        weekday: "long",
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      value: d,
    })),
  ];

  const availableSlots = useMemo(
    () =>
      selectedDate
        ? getAvailableSlotsForDate(
            selectedDate,
            product,
            bookedTimeslots,
            ecetTime,
          )
        : [],
    [selectedDate, product, bookedTimeslots, ecetTime],
  );

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate || null);
    setSelectedTimeslot(null);
    if (!newDate) onChange(null);
  };

  const handleTimeslotChange = (e) => {
    const newTimeslot = e.target.value;
    setSelectedTimeslot(newTimeslot);
    if (selectedDate && newTimeslot) {
      onChange(selectedDate + "T" + newTimeslot);
    }
  };

  return (
    <div>
      <Spin spinning={loading}>
        <DateListPicker
          value={selectedDate}
          onChange={handleDateChange}
          options={dayOptions}
          placeholder={$t("Selecteer een datum")}
        />
      </Spin>

      {selectedDate && (
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <p className="label" style={{ margin: 0 }}>
              {$t("Kies een tijdslot")}
            </p>
          </div>
          <Radio.Group
            options={availableSlots}
            value={selectedTimeslot}
            onChange={handleTimeslotChange}
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          />
          {selectedTimeslot && (
            <Button
              size="small"
              type="link"
              style={{ padding: 0, marginTop: 12 }}
              onClick={() => {
                setSelectedTimeslot(null);
                onChange(null);
              }}
            >
              {$t("Wissen")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
